import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";
import { sanitizeEmojiForStorage, sanitizeEmojiFromStorage, isValidEmoji, processEmoji } from "@/utils/emojiUtils";

type ChatMessage = Database['public']['Tables']['chat_messages']['Row'];
type MessageSticker = Database['public']['Tables']['message_stickers']['Row'];

interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

interface MessageStickerWithUser {
  id: string;
  emoji: string;
  userId: string;
  userName: string;
  count: number;
}

export function useChatMessages(roomId: string) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [userProfiles, setUserProfiles] = useState<Map<string, UserProfile>>(new Map());
  const [messageStickers, setMessageStickers] = useState<Map<string, MessageStickerWithUser[]>>(new Map());
  const [loading, setLoading] = useState(true);

  // Helper function to get user avatar URL
  const getUserAvatarUrl = (userId: string) => {
    const profile = userProfiles.get(userId);
    return profile?.avatar_url || null;
  };
  // Helper function to get user display name with fallback to RPC
  const getUserDisplayName = (userId: string) => {
    const profile = userProfiles.get(userId);
    if (profile?.first_name || profile?.last_name) {
      return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    }
    
    // Fallback: Try to get from profiles table directly since it's now public
    if (!userProfiles.has(userId)) {
      const fetchProfile = async () => {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, avatar_url')
            .eq('id', userId)
            .maybeSingle();
            
          if (error) {
            console.error('Failed to fetch profile:', error);
            return;
          }
          
          if (data) {
            setUserProfiles(current => new Map(current.set(userId, {
              id: data.id,
              first_name: data.first_name,
              last_name: data.last_name,
              avatar_url: data.avatar_url
            })));
          }
        } catch (err) {
          console.error('Failed to fetch profile:', err);
        }
      };
      fetchProfile();
    }
    
    return 'Unknown User';
  };

  // Database cleanup function
  const cleanupInvalidStickers = async () => {
    try {
      console.log('Starting sticker cleanup...');
      
      // Get all stickers to validate
      const { data: allStickers, error } = await supabase
        .from("message_stickers")
        .select("id, emoji");

      if (error) {
        console.error("Error fetching stickers for cleanup:", error);
        return;
      }

      const invalidStickerIds: string[] = [];
      
      allStickers?.forEach(sticker => {
        if (!isValidEmoji(sticker.emoji)) {
          console.log('Found invalid sticker:', sticker);
          invalidStickerIds.push(sticker.id);
        }
      });

      if (invalidStickerIds.length > 0) {
        console.log(`Cleaning up ${invalidStickerIds.length} invalid stickers`);
        
        const { error: deleteError } = await supabase
          .from("message_stickers")
          .delete()
          .in("id", invalidStickerIds);

        if (deleteError) {
          console.error("Error cleaning up invalid stickers:", deleteError);
        } else {
          console.log("Successfully cleaned up invalid stickers");
          toast.success(`Cleaned up ${invalidStickerIds.length} corrupted stickers`);
        }
      }
    } catch (err) {
      console.error("Error during sticker cleanup:", err);
    }
  };

  // Fetch message stickers with validation
  const fetchMessageStickers = async (messageIds: string[]) => {
    if (messageIds.length === 0) return;

    try {
      console.log("Fetching stickers for messages:", messageIds);
      
      const { data: stickers, error } = await supabase
        .from("message_stickers")
        .select(`
          id,
          message_id,
          emoji,
          user_id,
          created_at
        `)
        .in("message_id", messageIds);

      if (error) {
        console.error("Error fetching message stickers:", error);
        return;
      }

      console.log("Fetched stickers raw data:", stickers);

      if (stickers) {
        // Group stickers by message_id and aggregate by emoji and user
        const stickerMap = new Map<string, MessageStickerWithUser[]>();
        
        // Group by message_id, emoji, and user_id
        const grouped = stickers.reduce((acc, sticker) => {
          // Validate and process emoji
          if (!isValidEmoji(sticker.emoji)) {
            console.error("Invalid emoji found, skipping:", sticker.emoji);
            return acc; // Skip invalid emojis
          }

          const processedEmoji = sanitizeEmojiFromStorage(sticker.emoji);
          console.log("Processing sticker:", { original: sticker.emoji, processed: processedEmoji });
          
          const key = `${sticker.message_id}-${processedEmoji}-${sticker.user_id}`;
          if (!acc[key]) {
            acc[key] = {
              message_id: sticker.message_id,
              emoji: processedEmoji,
              user_id: sticker.user_id,
              stickers: []
            };
          }
          acc[key].stickers.push(sticker);
          return acc;
        }, {} as Record<string, { message_id: string; emoji: string; user_id: string; stickers: MessageSticker[] }>);

        // Convert to final format
        Object.values(grouped).forEach(group => {
          if (!stickerMap.has(group.message_id)) {
            stickerMap.set(group.message_id, []);
          }

          const messageStickers = stickerMap.get(group.message_id)!;
          messageStickers.push({
            id: group.stickers[0].id,
            emoji: group.emoji,
            userId: group.user_id,
            userName: getUserDisplayName(group.user_id),
            count: group.stickers.length
          });
        });

        console.log("Processed sticker map:", stickerMap);
        setMessageStickers(stickerMap);
      }
    } catch (err) {
      console.error("Error fetching message stickers:", err);
    }
  };

  // Fetch messages (works for both authenticated and unauthenticated users)
  useEffect(() => {
    if (!roomId) return;

    const fetchMessages = async () => {
      try {
        setLoading(true);
        
        // Clean up invalid stickers first
        if (user) {
          await cleanupInvalidStickers();
        }
        
        console.log("useChatMessages: Fetching messages for room:", roomId);
        
        const { data, error } = await supabase
          .from("chat_messages")
          .select(`
            *,
            reply_to:parent_message_id(
              id,
              content,
              sender_id,
              media_type,
              media_url,
              sent_at
            )
          `)
          .eq("room_id", roomId)
          .order("sent_at", { ascending: true });

        if (error) {
          console.error("useChatMessages: Error fetching messages:", error);
          toast.error("Failed to load messages");
          return;
        }

        console.log("useChatMessages: Fetched messages:", data?.length || 0, "messages");
        setMessages(data || []);

        // Fetch user profiles for all unique sender IDs directly from profiles table (now public)
        const senderIds = [...new Set(data?.map(msg => msg.sender_id).filter(Boolean) || [])];
        
        // Also get sender IDs from reply_to messages for complete profile coverage
        const replyToSenderIds = [...new Set(
          data?.filter(msg => msg.reply_to?.sender_id)
            .map(msg => msg.reply_to!.sender_id)
            .filter(Boolean) || []
        )];
        
        const allSenderIds = [...new Set([...senderIds, ...replyToSenderIds])];
        
        if (allSenderIds.length > 0) {
          console.log("🔍 Fetching profiles for all senders (including replied-to):", allSenderIds);
          
          try {
            // Direct query to profiles table since it's now publicly readable
            const { data: profiles, error: profileError } = await supabase
              .from('profiles')
              .select('id, first_name, last_name, avatar_url')
              .in('id', allSenderIds);
            
            if (profileError) {
              console.error("❌ Error fetching profiles:", profileError);
            }
            
            const profilesMap = new Map();
            (profiles || []).forEach(profile => {
              profilesMap.set(profile.id, {
                id: profile.id,
                first_name: profile.first_name,
                last_name: profile.last_name,
                avatar_url: profile.avatar_url
              });
            });
            
            console.log("✅ Fetched public profiles directly:", profilesMap);
            setUserProfiles(profilesMap);
          } catch (err) {
            console.error("❌ Error fetching public profiles:", err);
          }
          
          // Fetch stickers for messages after profiles are loaded
          const messageIds = data?.map(msg => msg.id) || [];
          await fetchMessageStickers(messageIds);
        }
      } catch (err) {
        console.error("useChatMessages: Error fetching messages:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [roomId]);

  // Real-time subscription using the proven conversation pattern
  useEffect(() => {
    if (!roomId) return;

    console.log(`🔴 REALTIME: Setting up subscription for room: ${roomId}`);

    const messagesChannel = supabase
      .channel(`room-messages-${roomId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `room_id=eq.${roomId}`
        },
        (payload) => {
          const newMessage = payload.new as ChatMessage;
          console.log("🟢 REALTIME: New message received!", newMessage.id);
          
          // Add message with duplicate check
          setMessages(prev => {
            const exists = prev.some(msg => msg.id === newMessage.id);
            if (exists) {
              console.log("🚫 REALTIME: Message already exists, skipping:", newMessage.id);
              return prev;
            }
            console.log("✅ REALTIME: Adding new message:", newMessage.id);
            return [...prev, newMessage];
          });

          // Fetch profile for new sender if needed - direct query since profiles are now public
          if (newMessage.sender_id && !userProfiles.has(newMessage.sender_id)) {
            console.log("🔄 FETCHING: Profile for new sender directly:", newMessage.sender_id);
            
            const fetchProfile = async () => {
              try {
                const { data, error } = await supabase
                  .from('profiles')
                  .select('id, first_name, last_name, avatar_url')
                  .eq('id', newMessage.sender_id)
                  .maybeSingle();
                
                if (error) {
                  console.error("❌ PROFILE FETCH ERROR:", error);
                  return;
                }
                
                if (data) {
                  console.log("✅ FETCHED: Profile directly for sender:", data);
                  setUserProfiles(current => new Map(current.set(data.id, {
                    id: data.id,
                    first_name: data.first_name,
                    last_name: data.last_name,
                    avatar_url: data.avatar_url
                  })));
                } else {
                  console.log("❌ NO PROFILE: Found for sender:", newMessage.sender_id);
                }
              } catch (err) {
                console.error("❌ PROFILE FETCH ERROR:", err);
              }
            };
            
            fetchProfile();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
          filter: `room_id=eq.${roomId}`
        },
        (payload) => {
          const updatedMessage = payload.new as ChatMessage;
          console.log("🟡 REALTIME: Message updated:", updatedMessage.id);
          
          setMessages(prev => prev.map(msg => 
            msg.id === updatedMessage.id ? updatedMessage : msg
          ));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'chat_messages',
          filter: `room_id=eq.${roomId}`
        },
        (payload) => {
          const deletedMessage = payload.old as ChatMessage;
          console.log("🔴 REALTIME: Message deleted:", deletedMessage.id);
          
          setMessages(prev => prev.filter(msg => msg.id !== deletedMessage.id));
        }
      )
      .subscribe((status) => {
        console.log(`🔗 REALTIME MESSAGES STATUS: ${status}`);
      });


    // Separate channel for stickers with comprehensive real-time handling
    const stickersChannel = supabase
      .channel(`room-stickers-${roomId}`, {
        config: {
          broadcast: { self: false }
        }
      })
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to ALL events - INSERT, DELETE, UPDATE
          schema: 'public',
          table: 'message_stickers'
        },
(payload) => {
          console.log("🎯 REALTIME STICKER EVENT:", payload.eventType, payload);
          
          // Room-based filtering at the handler level since we can't filter message_stickers directly by room_id
          const stickerData = (payload.new || payload.old) as MessageSticker;
          const currentMessageIds = messages.map(m => m.id);
          
          if (!stickerData?.message_id || !currentMessageIds.includes(stickerData.message_id)) {
            console.log("🚫 Sticker event not for current room messages, ignoring");
            return;
          }
          
          // Unified INSERT/DELETE handler - direct analog approach
          if (payload.eventType === 'INSERT') {
            const newSticker = payload.new as MessageSticker;
            console.log("🎯 REALTIME INSERT: Sticker added:", newSticker);
            
            if (!isValidEmoji(newSticker.emoji)) {
              console.error("Invalid emoji, ignoring:", newSticker.emoji);
              return;
            }

            const normalizedEmoji = sanitizeEmojiFromStorage(newSticker.emoji);
            
            // Update stickers immediately for real-time effect across all users
            setMessageStickers(prev => {
              const newMap = new Map(prev);
              const existing = newMap.get(newSticker.message_id) || [];
              
              // Remove existing sticker from this user (handles replacement via upsert)
              const withoutUserSticker = existing.filter(s => s.userId !== newSticker.user_id);
              
              // Add new sticker
              const newStickerEntry = {
                id: newSticker.id,
                emoji: normalizedEmoji,
                userId: newSticker.user_id,
                userName: getUserDisplayName(newSticker.user_id),
                count: 1
              };
              
              const updatedStickers = [...withoutUserSticker, newStickerEntry];
              updatedStickers.sort((a, b) => a.emoji.localeCompare(b.emoji));
              
              newMap.set(newSticker.message_id, updatedStickers);
              console.log('✅ Real-time sticker added for message:', newSticker.message_id);
              return newMap;
            });
            
          } else if (payload.eventType === 'DELETE') {
            const deletedSticker = payload.old as MessageSticker;
            console.log("🔴 REALTIME DELETE: Sticker deleted:", deletedSticker);
            
            // EXACT ANALOG of INSERT handler - same validation and processing
            if (!isValidEmoji(deletedSticker.emoji)) {
              console.error("Invalid emoji in deletion, ignoring:", deletedSticker.emoji);
              return;
            }

            // Use normalized emoji to match what was stored during INSERT (ensuring consistency)
            const normalizedEmoji = sanitizeEmojiFromStorage(deletedSticker.emoji);
            console.log("🔍 DELETE: Looking for sticker to remove:", {
              messageId: deletedSticker.message_id,
              userId: deletedSticker.user_id,
              originalEmoji: deletedSticker.emoji,
              normalizedEmoji
            });
            
            // Update stickers immediately for real-time effect across all users - exact same pattern as INSERT
            setMessageStickers(prev => {
              const newMap = new Map(prev);
              const existing = newMap.get(deletedSticker.message_id) || [];
              
              console.log("🔍 DELETE: Current stickers for message:", existing);
              
              // Remove the specific sticker by matching user_id and normalized emoji (consistent with INSERT processing)
              const filteredStickers = existing.filter(s => {
                const matches = s.userId === deletedSticker.user_id && s.emoji === normalizedEmoji;
                console.log("🔍 DELETE: Checking sticker:", { 
                  sticker: s, 
                  userMatch: s.userId === deletedSticker.user_id,
                  emojiMatch: s.emoji === normalizedEmoji,
                  willRemove: matches
                });
                return !matches;
              });
              
              console.log("🔍 DELETE: Filtered stickers:", filteredStickers);
              
              if (filteredStickers.length > 0) {
                newMap.set(deletedSticker.message_id, filteredStickers);
              } else {
                newMap.delete(deletedSticker.message_id);
              }
              
              console.log('✅ Real-time sticker removed for message:', deletedSticker.message_id);
              return newMap;
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'message_stickers'
        },
        (payload) => {
          const updatedSticker = payload.new as MessageSticker;
          console.log("🟡 REALTIME UPDATE: Sticker updated:", updatedSticker);
          
          if (!isValidEmoji(updatedSticker.emoji)) {
            console.error("Invalid emoji in update, ignoring:", updatedSticker.emoji);
            return;
          }

          const normalizedEmoji = sanitizeEmojiFromStorage(updatedSticker.emoji);
          
          // Handle sticker updates immediately for real-time effect
          setMessageStickers(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(updatedSticker.message_id) || [];
            
            // Remove existing sticker from this user and add updated one
            const withoutUserSticker = existing.filter(s => s.userId !== updatedSticker.user_id);
            
            const updatedStickerEntry = {
              id: updatedSticker.id,
              emoji: normalizedEmoji,
              userId: updatedSticker.user_id,
              userName: getUserDisplayName(updatedSticker.user_id),
              count: 1
            };
            
            const updatedStickers = [...withoutUserSticker, updatedStickerEntry];
            updatedStickers.sort((a, b) => a.emoji.localeCompare(b.emoji));
            
            newMap.set(updatedSticker.message_id, updatedStickers);
            console.log('✅ Real-time sticker updated for message:', updatedSticker.message_id);
            return newMap;
          });
        }
      )
      .subscribe((status) => {
        console.log(`🎯 REALTIME STICKERS STATUS: ${status}`);
        if (status === 'SUBSCRIBED') {
          console.log('🎯 Sticker real-time subscription active for room:', roomId);
        }
      });

    return () => {
      console.log(`🔄 REALTIME: Cleaning up subscriptions for room: ${roomId}`);
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(stickersChannel);
    };
  }, [roomId]); // Simple dependency like messages

  const sendMessage = async (content: string, mediaData?: {
    mediaUrl: string;
    mediaType: 'image' | 'video' | 'gif';
    mediaFilename: string;
    mediaSize: number;
  }, replyToId?: string) => {
    if (!user) {
      toast.error("You must be signed in to send messages");
      return;
    }

    if (!content.trim() && !mediaData) {
      toast.error("Message cannot be empty");
      return;
    }

    try {
      console.log("🚀 SEND MESSAGE: Starting message send", { roomId, userId: user.id });
      
      // First check if the room exists and get its privacy status
      const { data: roomData, error: roomError } = await supabase
        .from("chat_rooms")
        .select("id, is_private, created_by")
        .eq("id", roomId)
        .single();

      if (roomError || !roomData) {
        console.error("Room not found:", roomError);
        toast.error("Room not found");
        return;
      }

      console.log("Room data:", roomData);

      // Check if user is a member of private rooms
      if (roomData.is_private) {
        const { data: membershipData, error: membershipError } = await supabase
          .from("chat_room_members")
          .select("id, role")
          .eq("room_id", roomId)
          .eq("user_id", user.id)
          .single();

        console.log("Membership check result:", { membershipData, membershipError });
        
        if (membershipError && membershipError.code === 'PGRST116') {
          console.log("User is not a member of this private room");
          toast.error("You must be a member to send messages to this private room");
          return;
        }
      }

      // Use the actual content, allow empty strings for media-only messages
      const messageContent = content.trim();
      
      console.log("Final message content:", messageContent);

      const messageData = {
        room_id: roomId,
        sender_id: user.id,
        content: messageContent,
        parent_message_id: replyToId || null,
        ...(mediaData && {
          media_type: mediaData.mediaType,
          media_url: mediaData.mediaUrl,
          media_filename: mediaData.mediaFilename,
          media_size: mediaData.mediaSize
        })
      };

      console.log("📤 SEND MESSAGE: About to insert:", messageData);

      const { data: insertedMessage, error } = await supabase
        .from("chat_messages")
        .insert(messageData)
        .select()
        .single();

      if (error) {
        console.error("❌ SEND MESSAGE: Supabase error:", error);
        toast.error("Failed to send message: " + error.message);
        throw error;
      }

      console.log("✅ SEND MESSAGE: Message sent successfully!", insertedMessage);
      
      // No optimistic update needed - real-time subscription will handle adding the message
    } catch (err) {
      console.error("❌ SEND MESSAGE: General error:", err);
      toast.error("Failed to send message");
      throw err;
    }
  };

  const editMessage = async (messageId: string, newContent: string) => {
    if (!user) {
      toast.error("You must be signed in to edit messages");
      return false;
    }

    try {
      const { data, error } = await supabase.rpc('edit_chat_message', {
        message_id_param: messageId,
        new_content: newContent,
        user_id_param: user.id
      });

      if (error) {
        console.error("Error editing message:", error);
        toast.error("Failed to edit message");
        return false;
      }

      if (data) {
        toast.success("Message updated successfully");
        return true;
      } else {
        toast.error("You can only edit your own messages");
        return false;
      }
    } catch (err) {
      console.error("Error editing message:", err);
      toast.error("Failed to edit message");
      return false;
    }
  };

  const deleteMessage = async (messageId: string) => {
    if (!user) {
      toast.error("You must be signed in to delete messages");
      return false;
    }

    try {
      const { data, error } = await supabase.rpc('delete_chat_message', {
        message_id_param: messageId,
        user_id_param: user.id
      });

      if (error) {
        console.error("Error deleting message:", error);
        toast.error("Failed to delete message");
        return false;
      }

      if (data) {
        toast.success("Message deleted successfully");
        return true;
      } else {
        toast.error("You don't have permission to delete this message");
        return false;
      }
    } catch (err) {
      console.error("Error deleting message:", err);
      toast.error("Failed to delete message");
      return false;
    }
  };

  const addSticker = async (messageId: string, emoji: string) => {
    console.log("🔵 useChatMessages - addSticker called:", { messageId, emoji, userId: user?.id });
    
    if (!user) {
      console.log("❌ No user found, cannot add sticker");
      return false;
    }

    // Validate parameters to prevent UUID/emoji mix-up
    if (!messageId || !emoji) {
      console.error("❌ Invalid parameters for addSticker:", { messageId, emoji });
      return false;
    }

    // Extra validation to ensure we're not passing messageId as emoji
    if (messageId === emoji) {
      console.error("❌ MessageId and emoji are the same, preventing corruption:", { messageId, emoji });
      return false;
    }

    try {
      // Validate and sanitize emoji
      const emojiToStore = sanitizeEmojiForStorage(emoji);
      console.log("🔄 Adding sticker:", { messageId, originalEmoji: emoji, emojiToStore, userId: user.id });
      
      // With the new unique constraint, we use upsert to replace any existing sticker
      const { data, error } = await supabase
        .from("message_stickers")
        .upsert({
          message_id: messageId,
          user_id: user.id,
          emoji: emojiToStore
        }, {
          onConflict: 'user_id,message_id'
        })
        .select();

      if (error) {
        console.error("❌ Error adding/updating sticker:", error);
        return false;
      }

      console.log("✅ Sticker added/updated successfully:", data);
      return true;
    } catch (err) {
      console.error("❌ Error adding sticker:", err);
      return false;
    }
  };

  // Remove sticker - simplified to mirror addSticker pattern
  const removeSticker = async (messageId: string, emoji: string) => {
    if (!user) return;

    try {
      console.log("🗑️ Removing sticker:", { messageId, emoji, userId: user.id });
      
      // Process emoji for consistent comparison - same as addSticker
      const processedEmoji = processEmoji(emoji);
      const storedEmoji = sanitizeEmojiForStorage(processedEmoji);
      
      // Direct database deletion - same pattern as addSticker but DELETE instead of UPSERT
      const { error } = await supabase
        .from("message_stickers")
        .delete()
        .eq("message_id", messageId)
        .eq("user_id", user.id)
        .eq("emoji", storedEmoji);

      if (error) {
        console.error("❌ Failed to remove sticker:", error);
        toast.error("Failed to remove reaction");
        return;
      }

      console.log("✅ Sticker removed successfully - real-time will handle UI update");
    } catch (error) {
      console.error("❌ Error removing sticker:", error);
      toast.error("Failed to remove reaction");
    }
  };

  return {
    messages,
    loading,
    sendMessage,
    editMessage,
    deleteMessage,
    addSticker,
    removeSticker,
    messageStickers,
    getUserDisplayName,
    getUserAvatarUrl
  };
}
